import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConnectStatus from './ConnectStatus';
import { DanskeBankIcon } from '@/icons/DanskeBankIcon';
import { AiiaIcon } from '@/icons/AiiaIcon';
import { InstaPaidIcon } from '@/icons/InstaPaidIcon';
import { ExclamationMarkIcon } from '@/icons/ExclamationMarkIcon';
import {
    CompanyDetailsResponse,
    paymentProviders,
    CompanyDanskeBankSettings
} from '@/models/payment';
import {
    getCompanyDanskeBankSettingsData,
    updateCompanyDanskeBankSettingsInfo
} from '@/lib/slices/paymentsSlice';
import { IntegrationDialog } from './IntegrationDialog';
import { ConnectService } from './ConnectService';
import { useAppDispatch, usePayments } from '@/lib/hooks';
import { CompanySettingProps } from '@/models/company';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import { useErrorHandling } from '@/utils/errorHandling';
import { useLoader } from '@/contexts/loader/LoaderContext';
import { ConnectionStatus as ConnectStatusType } from '@/services/PaymentsServiceV2';
import { useFormatting } from '@/utils/formatting';
import { DATE_FORMAT } from '@/lib/constants';
interface ConnectPaymentServiceProps extends CompanySettingProps {
    reloadList: any;
}

export const ConnectPaymentService = (props: ConnectPaymentServiceProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { companyDanskeBankSettings } = usePayments();
    const { setAlert } = useNotificationsSnackbar();
    const { formatDate, toDecimal } = useFormatting();
    const [companyUpdateData, setCompanyUpdateData] =
        useState<CompanyDetailsResponse>({});
    const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
    const [connectServiceDialogOpen, setConnectServiceDialogOpen] =
        useState(false);
    const [editConnectServiceDialogOpen, setEditConnectServiceDialogOpen] =
        useState(false);
    const [connectUrl, setConnectUrl] = useState<string>();
    const { backendErrorHandler } = useErrorHandling();
    const [errorMessage, setErrorMessage] = useState<string>();
    const { setLoading } = useLoader();

    useEffect(() => {
        setCompanyUpdateData({
            paymentProvider: props?.companyDetails?.data?.paymentProvider,
            paymentSource: props?.companyDetails?.data?.paymentSource,
            aiiaPaymentsAllowedPerAuthorization:
                props?.companyDetails?.data
                    ?.aiiaPaymentsAllowedPerAuthorization,
            vatNumber: props?.companyDetails?.data?.vatNumber,
            approvalType: props?.companyDetails?.data?.approvalType
        });
    }, [props?.companyDetails]);

    useEffect(() => {
        if (props?.companyDetails?.data?.paymentProvider === 'DanskeBank')
            dispatch(getCompanyDanskeBankSettingsData());
    }, [props?.companyDetails]);

    const icon = useMemo(() => {
        if (props?.companyDetails?.data?.paymentProvider === 'DanskeBank')
            return <DanskeBankIcon />;
        if (props?.companyDetails?.data?.paymentProvider === 'Aiia')
            return <AiiaIcon />;
        if (props?.companyDetails?.data?.paymentProvider === 'None')
            return <InstaPaidIcon />;
        return <ExclamationMarkIcon />;
    }, [props?.companyDetails?.data?.paymentProvider]);

    const title = useMemo(() => {
        if (props?.companyDetails?.data?.paymentProvider === 'DanskeBank')
            return t('danske_bank');
        if (props?.companyDetails?.data?.paymentProvider === 'Aiia')
            return t('aiia');
        if (props?.companyDetails?.data?.paymentProvider === 'None')
            return t('instapaid_account');
        return t('connect_to_payment_service');
    }, [props?.companyDetails?.data?.paymentProvider]);

    const id = useMemo(() => {
        if (props?.companyDetails?.data?.paymentProvider === 'DanskeBank')
            return (
                companyDanskeBankSettings?.list?.iban &&
                `IBAN ${companyDanskeBankSettings?.list?.iban}`
            );
        if (props?.companyDetails?.data?.paymentProvider === 'Aiia')
            return props?.companyDetails?.data?.aiiaAccountId
                ? `Your bank allows batch payments of up to ${' '}
        ${
            props?.companyDetails?.data?.aiiaPaymentsAllowedPerAuthorization
        }${' '}
        payment(-s).`
                : '';
        if (props?.companyDetails?.data?.paymentProvider === 'None') return '';
        return '';
    }, [props?.companyDetails]);

    const status = useMemo(() => {
        if (props?.companyDetails?.data?.paymentProvider === 'DanskeBank')
            return companyDanskeBankSettings?.list?.iban
                ? ConnectStatusType.CONNECTED
                : ConnectStatusType.NOT_CONNECTED;
        if (props?.companyDetails?.data?.paymentProvider === 'Aiia')
            return props?.companyDetails?.data?.aiiaAccountId
                ? ConnectStatusType.CONNECTED
                : ConnectStatusType.NOT_CONNECTED;
        if (props?.companyDetails?.data?.paymentProvider === 'None')
            return ConnectStatusType.CONNECTED;
        return ConnectStatusType.NOT_CONNECTED;
    }, [props?.companyDetails, companyDanskeBankSettings]);

    const connectionString = useMemo(() => {
        if (
            props?.companyDetails?.data?.paymentProvider === 'None' &&
            props?.companyDetails?.data?.lastAutoTopupDate &&
            props?.companyDetails?.data?.lastAutoTopupAmount !== undefined
        ) {
            const formattedDate = formatDate(
                props.companyDetails.data.lastAutoTopupDate,
                `${DATE_FORMAT.DATE_MONTH_YEAR} HH:mm`
            );
            const formattedAmount = toDecimal(
                props.companyDetails.data.lastAutoTopupAmount,
                2
            );
            const currency = props?.companyDetails?.data?.currency || 'DKK';
            return `${formattedDate} - ${formattedAmount} ${currency} transferred`;
        }
        return undefined;
    }, [
        props?.companyDetails?.data?.paymentProvider,
        props?.companyDetails?.data?.lastAutoTopupDate,
        props?.companyDetails?.data?.lastAutoTopupAmount,
        props?.companyDetails?.data?.currency,
        formatDate,
        toDecimal
    ]);

    const handleConnectStatus = (fieldName: string) => {
        if (fieldName === 'icon') return icon;
        if (fieldName === 'title') return title;
        if (fieldName === 'id') return id;
        if (fieldName === 'status') return status;
    };

    const onHandleEdit = () => {
        setEditConnectServiceDialogOpen(true);
    };

    const handleIntegrationContinue = () => {
        setLoading(true);
        PaymentsAPIServiceV2.getInstance()
            .updateCompanyDetails({
                ...companyUpdateData,
                paymentSource:
                    companyUpdateData?.paymentProvider === 'None'
                        ? 'InstaPaidAccount'
                        : 'CompanyAccount'
            })
            .then(() => {
                setAlert('Payment settings updated successfully.', 'success');
                setIntegrationDialogOpen(false);
                setConnectServiceDialogOpen(true);
                props?.reloadList();
            })
            .catch(backendErrorHandler('Something went wrong'))
            .finally(() => {
                setLoading(false);
            });
    };

    const handleIntegrationCancel = () => {
        setIntegrationDialogOpen(false);
        setCompanyUpdateData({
            paymentProvider: props?.companyDetails?.data?.paymentProvider
        });
    };

    const onHandleClose = () => {
        setConnectServiceDialogOpen(false);
        setEditConnectServiceDialogOpen(false);
    };

    useEffect(() => {
        if (!connectUrl && !errorMessage) {
            setLoading(true);
            PaymentsAPIServiceV2.getInstance()
                .getAiiaConnectUrl()
                .then((response) => {
                    if (response && response.data) {
                        setConnectUrl(response.data.connectUrl);
                        setConnectServiceDialogOpen(false);
                    }
                })
                .catch(
                    backendErrorHandler(
                        'Could not get AiiA connect URL: ',
                        (e: Error) => {
                            setErrorMessage(
                                'Could not get AiiA connect URL: ' + e.message
                            );
                        }
                    )
                )
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [connectUrl, errorMessage, backendErrorHandler, setLoading]);

    const onHandleConnect = () => {
        setLoading(true);
        PaymentsAPIServiceV2.getInstance()
            .updateCompanyDetails({
                ...companyUpdateData,
                paymentSource:
                    companyUpdateData?.paymentProvider === 'None'
                        ? 'InstaPaidAccount'
                        : 'CompanyAccount'
            })
            .then(() => {
                setAlert('Payment settings updated successfully.', 'success');
                setConnectServiceDialogOpen(false);
                setEditConnectServiceDialogOpen(false);
                props?.reloadList();
            })
            .catch(backendErrorHandler('Something went wrong'))
            .finally(() => {
                setLoading(false);
            });
    };

    const connectIntegrationService = useMemo(() => {
        return (
            <ConnectStatus
                displayEdit
                status={handleConnectStatus('status') as any}
                icon={handleConnectStatus('icon') as any}
                title={handleConnectStatus('title') as any}
                id={handleConnectStatus('id') as any}
                handleEdit={() => onHandleEdit()}
                subTitle={t('payment_service')}
                handleEditIntegration={() => setIntegrationDialogOpen(true)}
                url={
                    companyUpdateData?.paymentProvider === 'Aiia' && connectUrl
                        ? connectUrl
                        : ''
                }
                connectionString={connectionString}
            />
        );
    }, [
        handleConnectStatus,
        companyDanskeBankSettings,
        companyUpdateData,
        connectUrl,
        connectionString
    ]);

    return (
        <>
            {connectIntegrationService}

            <IntegrationDialog
                title={t('choose_payment_service')}
                content={t('choose_payment_service_description')}
                dialogOpen={integrationDialogOpen}
                handleContinue={() => handleIntegrationContinue()}
                handleCancel={handleIntegrationCancel}
                showCancelButton
                options={paymentProviders
                    .map((paymentProvider) => ({
                        id: paymentProvider,
                        name:
                            paymentProvider === 'Aiia'
                                ? t('aiia')
                                : paymentProvider === 'DanskeBank'
                                ? t('danske_bank')
                                : paymentProvider === 'None'
                                ? t('instapaid')
                                : null,
                        description:
                            paymentProvider === 'Aiia'
                                ? t('aiia_description')
                                : paymentProvider === 'DanskeBank'
                                ? t('danske_bank_description')
                                : paymentProvider === 'None'
                                ? t('instapaid_description')
                                : null,
                        icon:
                            paymentProvider === 'Aiia' ? (
                                <AiiaIcon />
                            ) : paymentProvider === 'DanskeBank' ? (
                                <DanskeBankIcon />
                            ) : paymentProvider === 'None' ? (
                                <InstaPaidIcon />
                            ) : null,
                        selected: false
                    }))
                    .sort((a: any, b: any) => a.name?.localeCompare(b.name))}
                selectedOptionId={companyUpdateData?.paymentProvider ?? ''}
                setSelectedOptionId={(val: any) => {
                    setCompanyUpdateData({
                        ...companyUpdateData,
                        paymentProvider: val
                    });
                }}
            />

            <ConnectService
                width={'400px'}
                title={
                    props?.companyDetails?.data?.paymentProvider === 'Aiia' &&
                    props?.companyDetails?.data?.aiiaAccountId
                        ? t('you_are_connected_to', {
                              connectService:
                                  props?.companyDetails?.data?.paymentProvider
                          })
                        : props?.companyDetails?.data?.paymentProvider ===
                          'DanskeBank'
                        ? t('add_danske_bank')
                        : props?.companyDetails?.data?.paymentProvider ===
                          'None'
                        ? ''
                        : t('connect_to', {
                              connectService:
                                  props?.companyDetails?.data?.paymentProvider
                          })
                }
                content={
                    props?.companyDetails?.data?.paymentProvider ===
                    'DanskeBank' ? (
                        t('danske_bank_content')
                    ) : props?.companyDetails?.data?.paymentProvider ===
                      'None' ? (
                        t('instaAcc_connect_msg')
                    ) : props?.companyDetails?.data?.paymentProvider ===
                          'Aiia' &&
                      props?.companyDetails?.data?.aiiaAccountId ? (
                        <>
                            <b>Aiia</b> - Your bank allows batch payments of up
                            to{' '}
                            {
                                props?.companyDetails?.data
                                    ?.aiiaPaymentsAllowedPerAuthorization
                            }{' '}
                            payment(-s).{' '}
                        </>
                    ) : (
                        ''
                    )
                }
                content2={
                    props?.companyDetails?.data?.paymentProvider === 'Aiia' &&
                    props?.companyDetails?.data?.aiiaAccountId
                        ? t('connection_warning')
                        : ''
                }
                content3={
                    props?.companyDetails?.data?.paymentProvider === 'None'
                        ? t('instapaid_account')
                        : ''
                }
                dialogOpen={editConnectServiceDialogOpen}
                handleConnect={onHandleConnect}
                handleBack={onHandleClose}
                logo={
                    props?.companyDetails?.data?.paymentProvider === 'Aiia' &&
                    props?.companyDetails?.data?.aiiaAccountId ? (
                        <AiiaIcon />
                    ) : props?.companyDetails?.data?.paymentProvider ===
                      'DanskeBank' ? (
                        <DanskeBankIcon />
                    ) : props?.companyDetails?.data?.paymentProvider ===
                      'None' ? (
                        <InstaPaidIcon />
                    ) : null
                }
                buttonTitle1={
                    props?.companyDetails?.data?.paymentProvider === 'Aiia'
                        ? t('cancel')
                        : t('back')
                }
                buttonTitle2={
                    props?.companyDetails?.data?.paymentProvider === 'Aiia' &&
                    props?.companyDetails?.data?.aiiaAccountId
                        ? t('reconnect')
                        : props?.companyDetails?.data?.paymentProvider ===
                              'DanskeBank' ||
                          props?.companyDetails?.data?.paymentProvider ===
                              'None'
                        ? t('save')
                        : t('connect')
                }
                displayFields={
                    props?.companyDetails?.data?.paymentProvider ===
                        'DanskeBank' && true
                }
                url={
                    props?.companyDetails?.data?.paymentProvider === 'Aiia' &&
                    connectUrl
                        ? connectUrl
                        : ''
                }
                connectServiceName={
                    props?.companyDetails?.data?.paymentProvider
                }
            />
        </>
    );
};
